import { partition } from 'es-toolkit';

import { Component, MemberLine } from '@paragon/contract-library';
import { getAllRelativeGeometries } from '@paragon/relative-geometry';
import { notNullish } from '@paragon/utilities';

import { standardReferenceLineKeys } from '../../store/actions/deletionActions';
import {
  getAllReferencedLineGuidsForRelativeGeometry,
  getDirectDependencyGuidsForReferenceLine,
} from './getAllDependentLinesOfCalculatedLines';

// A similar function with the same name exists on the server. Consider porting any updates from one to the other.
export const hasInvalidRelativeGeometry = (component: Component) => {
  const referenceLines = component.referenceLines ?? [];
  const [memberLines, nonMemberLines] = partition(
    referenceLines,
    (line): line is MemberLine => line.lineType === 'Member',
  );
  const geometries = getAllRelativeGeometries(component);

  // Catches any cases where a non-member reference line references another reference line that doesn't exist
  for (const referenceLine of nonMemberLines) {
    if (
      getDirectDependencyGuidsForReferenceLine(referenceLine, geometries)
        .filter(notNullish)
        .some((lineGuid) => !referenceLines.find((line) => lineGuid === line.guid))
    ) {
      return true;
    }
  }

  // Catches any cases where a member reference line references a relative geometry that doesn't exist
  for (const referenceLine of memberLines) {
    if (!geometries.find((geometry) => referenceLine.memberGeometryGuid === geometry.guid)) {
      return true;
    }
  }

  // Catches any cases where a relative geometry references a reference line that doesn't exist
  for (const geometry of geometries) {
    const dependencyGuids = getAllReferencedLineGuidsForRelativeGeometry(geometry).filter(notNullish);
    if (dependencyGuids.some((guid) => !referenceLines.find((line) => line.guid === guid))) {
      return true;
    }
  }

  // Catches any cases where a relative geometry doesn't have corresponding member lines
  for (const geometry of geometries) {
    const memberLinesForGeometry = memberLines.filter((line) => line.memberGeometryGuid === geometry.guid);
    if (memberLinesForGeometry.length !== 3) {
      return true;
    }
  }

  // Catches any cases where a standard reference line is missing
  for (const key of standardReferenceLineKeys) {
    if (component[key] == null) continue;
    if (!referenceLines.find((line) => line.guid === component[key])) {
      return true;
    }
  }

  return false;
};
